import { useState, useEffect, useMemo } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { toast } from 'react-toastify';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';

import { useAuth } from '../../AuthContext';
import { tokens } from '../../theme';
import { buildUi } from '../global/uiTokens';
import {
  DetailRow,
  Loading,
  SectionCard,
  dialogPaperSx,
  fieldSx,
  outlineBtn,
  primaryBtn,
} from './userShared';

const STAFF_IMAGE_BASE = 'https://mindsinaction.com.na/assets/media/pictures/';

const initials = (first, last) =>
  `${first?.[0] || ''}${last?.[0] || ''}`.toUpperCase() || '?';

const Profile = ({ userId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === 'dark';
  const ui = useMemo(() => buildUi(colors, isDark), [colors, isDark]);

  const { getData, updateData } = useAuth();
  const [profile, setProfile] = useState({
    FirstName: '',
    LastName: '',
    email: '',
    department: '',
  });
  const [openProfileDialog, setOpenProfileDialog] = useState(false);
  const [formData, setFormData] = useState({ FirstName: '', LastName: '', email: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function fetchProfile() {
      try {
        const response = await getData('proccess.php', 'staffs', userId);
        if (response.success) {
          setProfile(response.data[0]);
          setFormData(response.data[0]);
        } else {
          toast.error(response.message || 'Failed to load profile');
        }
      } catch (err) {
        toast.error('Network error: Failed to fetch profile');
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchProfile();
  }, [userId, getData]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await updateData('proccess.php', 'staffs', formData, userId);
      if (response.success) {
        toast.success('Profile updated successfully');
        /**
         * updateData answers with the WHOLE table, so assigning it to profile
         * replaced the record with an array and every field below went blank
         * until the next reload. Keep the edited values instead.
         */
        setProfile((prev) => ({ ...prev, ...formData }));
        setOpenProfileDialog(false);
      } else {
        toast.error(response.message || 'Failed to update profile');
      }
    } catch (err) {
      toast.error('Network error: Failed to update profile');
      console.error('Profile update error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <SectionCard
        ui={ui}
        title="Profile details"
        hint="How you appear to the rest of the team."
        action={
          <Button
            startIcon={<EditNoteOutlinedIcon />}
            onClick={() => setOpenProfileDialog(true)}
            disabled={loading}
            sx={outlineBtn(ui)}
          >
            Edit
          </Button>
        }
      >
        {loading ? (
          <Loading ui={ui} label="Loading your profile…" />
        ) : (
          <>
            <Stack direction="row" spacing={2} alignItems="center" mb={2.5}>
              <Avatar
                src={profile.image ? `${STAFF_IMAGE_BASE}${profile.image}` : undefined}
                sx={{
                  width: 64,
                  height: 64,
                  fontSize: 22,
                  fontWeight: 700,
                  background: ui.gradient,
                  color: '#fff',
                }}
              >
                {initials(profile.FirstName, profile.LastName)}
              </Avatar>

              <Box minWidth={0}>
                <Typography variant="h4" fontWeight={800} color={ui.textPrimary} noWrap>
                  {`${profile.FirstName || ''} ${profile.LastName || ''}`.trim() || '—'}
                </Typography>
                <Typography variant="body2" color={ui.textSecondary} noWrap>
                  {profile.email || '—'}
                </Typography>
              </Box>
            </Stack>

            <Divider sx={{ borderColor: ui.border, mb: 1 }} />

            <DetailRow ui={ui} label="First name" value={profile.FirstName} />
            <DetailRow ui={ui} label="Last name" value={profile.LastName} />
            <DetailRow ui={ui} label="Email" value={profile.email} />
            <DetailRow ui={ui} label="Department" value={profile.department} />
            {profile.role && <DetailRow ui={ui} label="Role" value={profile.role} />}
          </>
        )}
      </SectionCard>

      {/* ---------------- Edit ---------------- */}
      <Dialog
        open={openProfileDialog}
        onClose={(e, reason) => {
          // Not on a stray click outside - people lose what they typed.
          if (reason === 'backdropClick') return;
          setOpenProfileDialog(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx(ui) }}
      >
        <DialogTitle
          sx={{
            color: ui.textPrimary,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <BadgeOutlinedIcon sx={{ color: ui.accent }} />
          Edit profile
        </DialogTitle>
        <Divider sx={{ borderColor: ui.border }} />

        <DialogContent sx={{ pt: 3 }}>
          <Box
            component="form"
            onSubmit={handleProfileSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2.2 }}
          >
            {/* The name inputs used to be called firstName / lastName while the
                state keys are FirstName / LastName, so every keystroke wrote a
                field nothing read and the boxes appeared frozen. */}
            <TextField
              label="First name"
              name="FirstName"
              value={formData.FirstName || ''}
              onChange={handleFormChange}
              required
              sx={fieldSx(ui)}
            />
            <TextField
              label="Last name"
              name="LastName"
              value={formData.LastName || ''}
              onChange={handleFormChange}
              required
              sx={fieldSx(ui)}
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email || ''}
              onChange={handleFormChange}
              required
              sx={fieldSx(ui)}
            />
          </Box>
        </DialogContent>

        <Divider sx={{ borderColor: ui.border }} />
        <DialogActions sx={{ px: 3, py: 2.5 }}>
          <Button
            onClick={() => setOpenProfileDialog(false)}
            sx={{ textTransform: 'none', color: ui.textSecondary }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={handleProfileSubmit}
            disabled={saving}
            sx={primaryBtn(ui)}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;
